import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { extractJobErrorMessage } from '../../core/services/api';
import { resources } from '../../core/services/resources';
import type {
  DocumentSection,
  DocumentSectionEntity,
  DocumentSectionRelation,
  Entity,
  EntityRelation,
} from '../../core/types/data-models';

const MAX_ENTITIES_PAGE_SIZE = 1000;

/**
 * The section-scoped link rows this panel works in, fetched with their project
 * `Entity` / `EntityRelation` eagerly included — the panel is a view of what
 * one document says, so it lists mentions, not the project's entity set.
 */
type EntityMention = DocumentSectionEntity & { entity: Entity };
type RelationMention = DocumentSectionRelation & { entityRelation: EntityRelation };

// ── Colour palette per entity type ──────────────────────────────────────────

const ENTITY_TYPE_COLORS: Record<string, string> = {
  PERSON: '#1976d2',
  ORGANIZATION: '#7b1fa2',
  LOCATION: '#388e3c',
  EVENT: '#f57c00',
  CONCEPT: '#0288d1',
  PRODUCT: '#c2185b',
  OTHER: '#616161',
};

function entityTypeColor(type: string): string {
  return ENTITY_TYPE_COLORS[type.toUpperCase()] ?? ENTITY_TYPE_COLORS.OTHER;
}

// ── Section hierarchy helpers ───────────────────────────────────────────────

interface FlatSection {
  section: DocumentSection;
  depth: number;
}

/** Flattens the section parent/child hierarchy into depth-first order so it can be rendered as an indented tree. */
function flattenSectionTree(sections: DocumentSection[]): FlatSection[] {
  const childrenByParent = new Map<number | null, DocumentSection[]>();
  sections.forEach((s) => {
    const key = s.parentDocumentSectionId;
    const arr = childrenByParent.get(key) ?? [];
    arr.push(s);
    childrenByParent.set(key, arr);
  });
  childrenByParent.forEach((arr) => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

  const result: FlatSection[] = [];
  const visit = (parentId: number | null, depth: number) => {
    for (const s of childrenByParent.get(parentId) ?? []) {
      result.push({ section: s, depth });
      visit(s.id, depth + 1);
    }
  };
  visit(null, 0);
  return result;
}

// ── Table tab ────────────────────────────────────────────────────────────────

interface EntityRowProps {
  mention: EntityMention;
  relations: RelationMention[];
  /** Every project entity on screen, keyed by `Entity.id` — resolves relation endpoints. */
  entityById: Map<number, Entity>;
  sectionTitle: string;
}

function EntityRow({ mention, relations, entityById, sectionTitle }: EntityRowProps) {
  const [open, setOpen] = useState(false);
  const entity = mention.entity;

  const outgoing = relations.filter((r) => r.entityRelation.sourceEntityId === entity.id);
  const incoming = relations.filter((r) => r.entityRelation.targetEntityId === entity.id);
  const hasRelations = outgoing.length > 0 || incoming.length > 0;
  const hasDetails = !!entity.entityDescription || !!mention.spanText || hasRelations;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => hasDetails && setOpen((o) => !o)}
        sx={{
          px: 1.5,
          py: 1,
          cursor: hasDetails ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': hasDetails ? { bgcolor: 'action.hover' } : {},
        }}
      >
        <Box sx={{ width: 20, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {hasDetails && (
            open
              ? <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
        </Box>

        <Chip
          label={entity.entityType}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: entityTypeColor(entity.entityType),
            color: '#fff',
            fontWeight: 700,
            flexShrink: 0,
          }}
        />

        <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0 }}>
          {entity.entityName}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {sectionTitle}
        </Typography>

        {hasRelations && (
          <Chip
            label={`${outgoing.length + incoming.length} relations`}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
          />
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 6, pr: 2, pb: 1.5 }}>
          {entity.entityDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {entity.entityDescription}
            </Typography>
          )}
          {mention.spanText && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25 }}>
                Source text
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                “{mention.spanText}”
              </Typography>
            </Box>
          )}
          {outgoing.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25 }}>
                Outgoing relations
              </Typography>
              <Stack spacing={0.5}>
                {outgoing.map((r) => {
                  const target = entityById.get(r.entityRelation.targetEntityId);
                  return (
                    <Stack key={r.id} direction="row" alignItems="center" spacing={1}>
                      <Chip label={r.entityRelation.relationType} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                      <Typography variant="caption">→ {target?.entityName ?? `#${r.entityRelation.targetEntityId}`}</Typography>
                      {r.entityRelation.relationDescription && (
                        <Typography variant="caption" color="text.secondary">— {r.entityRelation.relationDescription}</Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
          {incoming.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25 }}>
                Incoming relations
              </Typography>
              <Stack spacing={0.5}>
                {incoming.map((r) => {
                  const source = entityById.get(r.entityRelation.sourceEntityId);
                  return (
                    <Stack key={r.id} direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption">{source?.entityName ?? `#${r.entityRelation.sourceEntityId}`} →</Typography>
                      <Chip label={r.entityRelation.relationType} size="small" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
                      {r.entityRelation.relationDescription && (
                        <Typography variant="caption" color="text.secondary">— {r.entityRelation.relationDescription}</Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Graph tab ─────────────────────────────────────────────────────────────────

interface GraphNode {
  id: number;
  label: string;
  type: string;
  x: number;
  y: number;
}

interface GraphEdge {
  id: number;
  source: number;
  target: number;
  label: string;
  description: string | null;
}

interface TooltipInfo {
  x: number;
  y: number;
  lines: string[];
}

function EntitiesGraph({
  entities,
  relations,
}: {
  /** Project entities, already deduped — one node per entity however many sections mention it. */
  entities: Entity[];
  relations: EntityRelation[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<TooltipInfo | null>(null);
  const [dragging, setDragging] = useState<{ id: number; ox: number; oy: number } | null>(null);
  const [positions, setPositions] = useState<Map<number, { x: number; y: number }>>(new Map());

  // Layout: arrange nodes in a circle, or a grid for many nodes
  const nodes: GraphNode[] = useMemo(() => {
    const W = 800;
    const H = 500;
    const cx = W / 2;
    const cy = H / 2;
    const r = Math.min(cx, cy) - 80;
    return entities.map((e, i) => {
      const angle = (2 * Math.PI * i) / entities.length - Math.PI / 2;
      const px = positions.get(e.id);
      return {
        id: e.id,
        label: e.entityName,
        type: e.entityType,
        x: px ? px.x : entities.length === 1 ? cx : cx + r * Math.cos(angle),
        y: px ? px.y : entities.length === 1 ? cy : cy + r * Math.sin(angle),
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, positions]);

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const entitiesById = new Map(entities.map((e) => [e.id, e]));

  const edges: GraphEdge[] = relations
    .filter((r) => nodeById.has(r.sourceEntityId) && nodeById.has(r.targetEntityId))
    .map((r) => ({
      id: r.id,
      source: r.sourceEntityId,
      target: r.targetEntityId,
      label: r.relationType,
      description: r.relationDescription,
    }));


  const handleMouseDown = (e: React.MouseEvent<SVGCircleElement>, nodeId: number) => {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const node = nodeById.get(nodeId)!;
    setDragging({ id: nodeId, ox: svgP.x - node.x, oy: svgP.y - node.y });
    setTooltip(null);
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragging) return;
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    setPositions((prev) => {
      const next = new Map(prev);
      next.set(dragging.id, { x: svgP.x - dragging.ox, y: svgP.y - dragging.oy });
      return next;
    });
  };

  const handleMouseUp = () => setDragging(null);

  const showNodeTooltip = (e: React.MouseEvent<SVGElement>, node: GraphNode, entity: Entity) => {
    if (dragging) return;
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const lines = [
      `${entity.entityName} (${entity.entityType})`,
      ...(entity.entityDescription ? [entity.entityDescription] : []),
    ];
    setTooltip({ x: svgP.x + 12, y: svgP.y - 8, lines });
  };

  const showEdgeTooltip = (e: React.MouseEvent<SVGElement>, edge: GraphEdge) => {
    const svg = svgRef.current;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    const src = nodeById.get(edge.source);
    const tgt = nodeById.get(edge.target);
    const lines = [
      `${src?.label ?? '?'} → ${tgt?.label ?? '?'}`,
      `Type: ${edge.label}`,
      ...(edge.description ? [edge.description] : []),
    ];
    setTooltip({ x: svgP.x + 12, y: svgP.y - 8, lines });
  };

  if (entities.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <Typography color="text.secondary">No entities to display.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', userSelect: 'none' }}>
      <svg
        ref={svgRef}
        viewBox="0 0 800 500"
        style={{ width: '100%', height: 'auto', display: 'block', cursor: dragging ? 'grabbing' : 'default' }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#90a4ae" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge) => {
          const s = nodeById.get(edge.source)!;
          const t = nodeById.get(edge.target)!;
          const mx = (s.x + t.x) / 2;
          const my = (s.y + t.y) / 2;
          // shorten line so it doesn't overlap nodes (r=28)
          const dx = t.x - s.x;
          const dy = t.y - s.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const r = 28;
          const x1 = s.x + (dx / len) * r;
          const y1 = s.y + (dy / len) * r;
          const x2 = t.x - (dx / len) * (r + 6); // account for arrowhead
          const y2 = t.y - (dy / len) * (r + 6);
          return (
            <g key={edge.id}>
              <line
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#90a4ae"
                strokeWidth={1.5}
                markerEnd="url(#arrowhead)"
              />
              {/* Invisible wider hit area */}
              <line
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="transparent"
                strokeWidth={10}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => showEdgeTooltip(e, edge)}
                onMouseLeave={() => setTooltip(null)}
              />
              <text
                x={mx}
                y={my - 5}
                textAnchor="middle"
                fontSize={9}
                fill="#78909c"
                pointerEvents="none"
              >
                {edge.label}
              </text>
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const entity = entitiesById.get(node.id)!;
          const color = entityTypeColor(node.type);
          return (
            <g key={node.id} transform={`translate(${node.x},${node.y})`}>
              <circle
                r={28}
                fill={color}
                fillOpacity={0.15}
                stroke={color}
                strokeWidth={2}
                style={{ cursor: 'grab' }}
                onMouseDown={(e) => handleMouseDown(e, node.id)}
                onMouseEnter={(e) => showNodeTooltip(e, node, entity)}
                onMouseLeave={() => !dragging && setTooltip(null)}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={10}
                fontWeight="bold"
                fill={color}
                pointerEvents="none"
                style={{ maxWidth: 48 }}
              >
                {node.label.length > 10 ? node.label.slice(0, 9) + '…' : node.label}
              </text>
              <text
                y={38}
                textAnchor="middle"
                fontSize={8}
                fill="#78909c"
                pointerEvents="none"
              >
                {node.type}
              </text>
            </g>
          );
        })}

        {/* Tooltip */}
        {tooltip && (
          <g>
            <rect
              x={tooltip.x - 4}
              y={tooltip.y - 14}
              width={300}
              height={tooltip.lines.length * 16 + 8}
              rx={4}
              fill="#263238"
              fillOpacity={0.9}
              pointerEvents="none"
            />
            {tooltip.lines.map((line, i) => (
              <text
                key={i}
                x={tooltip.x + 4}
                y={tooltip.y + i * 16}
                fontSize={10}
                fill="#fff"
                pointerEvents="none"
              >
                {line}
              </text>
            ))}
          </g>
        )}
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
        Drag nodes to rearrange. Hover nodes or edges to see details.
      </Typography>
    </Box>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

/** Entities tab content for DocumentDetail — filters, table/graph view, and the
 * regenerate-entities action. Embedded directly in the document page, so it does
 * not fetch or render the document header (the parent already owns that). */
export function DocumentEntitiesPanel({ docId }: { docId: number }) {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSection, setFilterSection] = useState<number | ''>('');
  const [sortBy, setSortBy] = useState<'entityName' | 'sectionName' | 'entityType'>('entityType');

  // ── Regenerate entities ──
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenJobId, setRegenJobId] = useState<number | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);

  // Poll the extraction job so the button shows progress until the job
  // (which replaces this document's entity links) finishes, then refresh the
  // data below.
  const regenJobQuery = useQuery({
    queryKey: ['job', regenJobId],
    queryFn: () => resources.getJob(regenJobId!),
    enabled: regenJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false;
    },
  });
  const regenJobStatus = regenJobQuery.data?.status;
  const regenerating = regenJobId != null && regenJobStatus !== 'COMPLETED' && regenJobStatus !== 'FAILED';

  useEffect(() => {
    if (regenJobStatus === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['document-entities', docId] });
      queryClient.invalidateQueries({ queryKey: ['document-relations', docId] });
    } else if (regenJobStatus === 'FAILED') {
      setRegenError(regenJobQuery.data?.error ?? 'Failed to regenerate entities');
    }
  }, [regenJobStatus, regenJobQuery.data?.error, docId, queryClient]);

  const handleRegenerate = async () => {
    setRegenConfirmOpen(false);
    setRegenError(null);
    try {
      const { jobId } = await resources.extractDocumentEntities(docId);
      setRegenJobId(jobId);
    } catch (err) {
      setRegenError(extractJobErrorMessage(err));
    }
  };

  // ── Delete entities ──
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteEntities = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setDeleteError(null);
    try {
      await resources.deleteDocumentEntities(docId);
      await queryClient.invalidateQueries({ queryKey: ['document-entities', docId] });
      await queryClient.invalidateQueries({ queryKey: ['document-relations', docId] });
    } catch (err) {
      setDeleteError(extractJobErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // ── Queries ──

  const sectionsQuery = useQuery({
    queryKey: ['document-sections', docId],
    queryFn: () =>
      resources.documentSections.list({
        filter: { documentId: [docId] },
        pageSize: 200,
      }),
    enabled: Number.isFinite(docId),
  });

  // Both link tables are fetched with their project entity / relation included:
  // the ids alone carry no name or type to display, filter or sort on.
  const entitiesQuery = useQuery({
    queryKey: ['document-entities', docId],
    queryFn: () =>
      resources.documentSectionEntities.list({
        filter: { documentId: [docId] },
        include: ['entity'],
        pageSize: MAX_ENTITIES_PAGE_SIZE,
      }),
    enabled: Number.isFinite(docId),
  });

  const relationsQuery = useQuery({
    queryKey: ['document-relations', docId],
    queryFn: () =>
      resources.documentSectionRelations.list({
        filter: { documentId: [docId] },
        include: ['entityRelation'],
        pageSize: MAX_ENTITIES_PAGE_SIZE,
      }),
    enabled: Number.isFinite(docId),
  });

  const sections: DocumentSection[] = sectionsQuery.data?.data ?? [];
  const allMentions = (entitiesQuery.data?.data ?? []) as EntityMention[];
  const allRelations = (relationsQuery.data?.data ?? []) as RelationMention[];

  const sectionTitleById = new Map<number, string>(sections.map((s) => [s.id, s.title]));
  // A document-level mention (no section) is labelled as such rather than left blank.
  const sectionTitleFor = (sectionId: number | null) =>
    sectionId === null ? 'Whole document' : sectionTitleById.get(sectionId) ?? '';
  // Keyed by Entity.id, not by mention id — that is what a relation's endpoints name.
  const entityById = new Map<number, Entity>(allMentions.map((m) => [m.entity.id, m.entity]));

  // ── Filter ──

  const entityTypes = useMemo(
    () => Array.from(new Set(allMentions.map((m) => m.entity.entityType))).sort(),
    [allMentions],
  );
  const sectionTree = useMemo(() => flattenSectionTree(sections), [sections]);

  const filteredMentions = useMemo(() => {
    const filtered = allMentions.filter((m) => {
      if (filterType && m.entity.entityType !== filterType) return false;
      if (filterName && !m.entity.entityName.toLowerCase().includes(filterName.toLowerCase())) return false;
      if (filterSection !== '' && m.documentSectionId !== filterSection) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      if (sortBy === 'sectionName') {
        const sectionCompare = sectionTitleFor(a.documentSectionId).localeCompare(sectionTitleFor(b.documentSectionId));
        if (sectionCompare !== 0) return sectionCompare;
      } else if (sortBy === 'entityType') {
        const typeCompare = a.entity.entityType.localeCompare(b.entity.entityType);
        if (typeCompare !== 0) return typeCompare;
      }
      return a.entity.entityName.localeCompare(b.entity.entityName);
    });
  }, [allMentions, filterType, filterName, filterSection, sortBy, sectionTitleFor]);

  // The graph is a view of entities, not of mentions: several sections
  // mentioning one entity are one node, so the filtered mentions collapse by
  // Entity.id before the relations linking within that set are kept.
  const filteredEntities = Array.from(
    new Map(filteredMentions.map((m) => [m.entity.id, m.entity])).values(),
  );
  const filteredEntityIds = new Set(filteredEntities.map((e) => e.id));
  const filteredRelations = Array.from(
    new Map(
      allRelations
        .filter(
          (r) =>
            filteredEntityIds.has(r.entityRelation.sourceEntityId) &&
            filteredEntityIds.has(r.entityRelation.targetEntityId),
        )
        .map((r) => [r.entityRelation.id, r.entityRelation]),
    ).values(),
  );

  const isLoading = entitiesQuery.isLoading || sectionsQuery.isLoading;

  // ── Render ──

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5 }}>
      {/* ── Filters ── */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search by name…"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Entity type</InputLabel>
          <Select
            label="Entity type"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value=""><em>All types</em></MenuItem>
            {entityTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Section</InputLabel>
          <Select
            label="Section"
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value as number | '')}
          >
            <MenuItem value=""><em>All sections</em></MenuItem>
            {sectionTree.map(({ section: s, depth }) => (
              <MenuItem key={s.id} value={s.id} sx={{ pl: 2 + depth * 2.5 }}>
                {depth > 0 && (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.75, fontSize: 13 }}
                  >
                    ↳
                  </Box>
                )}
                {s.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'entityName' | 'sectionName' | 'entityType')}
          >
            <MenuItem value="entityType">Entity type</MenuItem>
            <MenuItem value="entityName">Entity name</MenuItem>
            <MenuItem value="sectionName">Section name</MenuItem>
          </Select>
        </FormControl>

        {(filterName || filterType || filterSection !== '') && (
          <Tooltip title="Clear filters">
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => { setFilterName(''); setFilterType(''); setFilterSection(''); }}
            >
              Clear
            </Button>
          </Tooltip>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
          {filteredMentions.length} entit{filteredMentions.length === 1 ? 'y' : 'ies'}
          {' · '}
          {filteredRelations.length} relation{filteredRelations.length === 1 ? '' : 's'}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {regenerating && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              {regenJobQuery.data?.progressMessage ?? 'Preparing…'}
            </Typography>
          </Stack>
        )}
        {regenError && (
          <Typography variant="caption" color="error">{regenError}</Typography>
        )}
        {deleteError && (
          <Typography variant="caption" color="error">{deleteError}</Typography>
        )}
        <Tooltip title="Delete all of this document's entities and relations">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={deleting || regenerating || allMentions.length === 0}
            >
              Delete Entities
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Extract this document's entities from its content">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={() => setRegenConfirmOpen(true)}
              disabled={regenerating}
            >
              Regenerate Entities
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* ── Tabs ── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Table" />
        <Tab label="Graph" />
      </Tabs>

      {/* ── Loading skeleton ── */}
      {isLoading && (
        <Stack spacing={1}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} height={48} sx={{ borderRadius: 1 }} />)}
        </Stack>
      )}

      {/* ── No data ── */}
      {!isLoading && allMentions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No entities found for this document. Run entity extraction to populate this list.
        </Typography>
      )}

      {/* ── Table tab ── */}
      {!isLoading && allMentions.length > 0 && tab === 0 && (
        <Paper sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {filteredMentions.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">No entities match the current filters.</Typography>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider />}>
              {filteredMentions.map((mention) => (
                <EntityRow
                  key={mention.id}
                  mention={mention}
                  relations={allRelations.filter(
                    (r) =>
                      r.entityRelation.sourceEntityId === mention.entity.id ||
                      r.entityRelation.targetEntityId === mention.entity.id,
                  )}
                  entityById={entityById}
                  sectionTitle={sectionTitleFor(mention.documentSectionId)}
                />
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {/* ── Graph tab ── */}
      {!isLoading && allMentions.length > 0 && tab === 1 && (
        <Paper sx={{ p: 2, flex: 1, minHeight: 0, overflow: 'auto' }}>
          <EntitiesGraph entities={filteredEntities} relations={filteredRelations} />
        </Paper>
      )}

      {/* ── Regenerate confirmation ── */}
      <Dialog open={regenConfirmOpen} onClose={() => setRegenConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate entities?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This replaces this document's existing entity links with freshly extracted
            entities. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRegenerate} color="primary" variant="contained" size="small">
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete all entities?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes all {allMentions.length} entit{allMentions.length === 1 ? 'y' : 'ies'} and
            their relations for this document. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEntities} color="error" variant="contained" size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
